"use client";

import { Button } from "@/components/ui/button";
import { pageTitles } from "@/dictionaries/pageTitle/page-titles";
import { cn } from "@/lib/utils";
import { appUrl } from "@/utils/app-url";
import { pagesPath } from "@/utils/pages-path";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ReactNode, useMemo } from "react";
import { AccountActionMenu } from "./AccountActionMenu";

const adminPathPrefix = pagesPath.admin.$url().pathname;

/** usePathname() の値を /admin 始まりに正規化する */
function toAdminPath(pathname: string): string {
  if (
    pathname === adminPathPrefix ||
    pathname.startsWith(`${adminPathPrefix}/`)
  ) {
    return pathname;
  }
  if (pathname === "/") {
    return adminPathPrefix;
  }
  return `${adminPathPrefix}${pathname}`;
}

/** 辞書から admin 配下のページタイトルだけ抜き出す */
function getAdminPageTitles() {
  const pages: Record<string, string> = {};
  for (const key in pageTitles) {
    if (key.startsWith(adminPathPrefix)) {
      pages[key] = pageTitles[key as keyof typeof pageTitles];
    }
  }
  return pages;
}
const adminPageTitles = getAdminPageTitles();

/**
 * 動的セグメント（[fiscalYear] 等）を含むタイトルテンプレートの
 * プレースホルダ（{fiscalYear}）を、実パスの対応セグメント値で置換する。
 * 対応する値が見つからない場合はプレースホルダのまま残す。
 */
const resolveTitlePlaceholders = (
  title: string,
  templateKey: string,
  href: string,
): string => {
  const templateSegments = templateKey.split("/");
  const hrefSegments = href.split("/");
  return title.replace(/\{(\w+)\}/g, (match, paramName) => {
    const index = templateSegments.findIndex(
      (segment) => segment === `[${paramName}]`,
    );
    const value = index >= 0 ? hrefSegments[index] : undefined;
    return value ?? match;
  });
};

/**
 * 現在 URL のセグメントを先頭から積み上げ、各深さで辞書に該当があればパンくず項目にする。
 * 返り値は浅い順。key は辞書キー（テンプレートパス）、href は実パス。
 */
const useBreadcrumbs = (pathname: string) => {
  return useMemo(() => {
    const adminPathname = toAdminPath(pathname);
    const segments = adminPathname.split("/").filter(Boolean);
    const items: Array<{ key: string; href: string }> = [];

    for (let i = 1; i <= segments.length; i++) {
      const prefix = "/" + segments.slice(0, i).join("/");

      if (prefix in adminPageTitles) {
        items.push({ key: prefix, href: prefix });
        continue;
      }

      // 同じ深さの動的キー（[officeId] 等）を正規表現で探す
      const dynamicKey = Object.keys(adminPageTitles).find((key) => {
        if (key.split("/").filter(Boolean).length !== i) {
          return false;
        }
        const pattern = new RegExp(
          `^${key.replace(/\[([^[\]/]*)\]/g, "([^/\\[\\]]+)")}$`,
        );
        return pattern.test(prefix);
      });
      if (dynamicKey) {
        items.push({ key: dynamicKey, href: prefix });
      }
    }

    return items;
  }, [pathname]);
};

export const Navigation = ({
  children,
  currentStaffName,
}: {
  children: ReactNode;
  currentStaffName: string;
}) => {
  const pathname = usePathname();
  const adminPathname = toAdminPath(pathname);
  const breadcrumbs = useBreadcrumbs(pathname);

  return (
    <>
      <div className="m-4 flex flex-row justify-between">
        <div className="m-4 flex w-fit flex-row text-sm">
          {breadcrumbs.map((item, index) => {
            const rawTitle =
              item.key === adminPathPrefix ? "Top" : adminPageTitles[item.key];
            if (!rawTitle) {
              return null;
            }
            // 動的ルートのタイトルに含まれるプレースホルダを実パスの値で置換する
            const title = resolveTitlePlaceholders(
              rawTitle,
              item.key,
              item.href,
            );

            return (
              <Button
                key={index}
                variant="link"
                className={cn(
                  "mx-1 flex min-w-[144px] items-center justify-center rounded-full px-3 py-2 font-bold text-white hover:no-underline",
                  item.href === adminPathname ? "bg-primary-4" : "bg-primary-2",
                )}
                asChild
              >
                <Link href={appUrl(item.href)}>{title}</Link>
              </Button>
            );
          })}
        </div>
        <div className="m-4">
          <AccountActionMenu userName={currentStaffName} />
        </div>
      </div>
      {children}
    </>
  );
};
